import { useGetFormattedDate } from '@app/src/components/DateDisplay'
import FileAnswerContent from '@app/src/components/FileAnswerContent'
import FlagIndicator from '@app/src/components/Flags/FlagIndicator'
import { usePreviousNumberAnswers } from '@app/src/context/PreviousNumberAnswersContext'
import SkippedQuestionCard from '@app/src/pages/Questionnaire/Provider/Report/SkippedQuestionCard'
import DeviationIndicatorAnswer from '@app/src/pages/ResourceCollection/Collections/DataHub/AllAnswers/DeviationIndicatorAnswer'
import { palette } from '@app/src/theme/palette'
import { ReportFlagType } from '@app/src/types/flags'
import { QuestionType } from '@app/src/types/reporting'
import type { CoordinateLocation, ResponseItem } from '@app/src/types/resourceExplorer'
import { equalsCaseInsensitive, insertObjIf } from '@app/src/utils/helpersTs'
import { QuestionTypes, WF_ORGANIZATION_ID } from '@app/src/wf-constants'
import { AutoAwesomeOutlined, ContentCopy, DateRangeOutlined } from '@mui/icons-material'
import { alpha, Box, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import TextTruncate from 'react-text-truncate'
import { DRAWER_WIDTH, useDrawer } from '../../Drawer/DrawerContext'
import DrawerViewAiScreenedAnswerDetail from '../../Drawer/Views/DrawerViewAiScreenedAnswerDetail'
import DrawerViewAnswerDetails from '../../Drawer/Views/DrawerViewAnswerDetails'
import ParentCellOrFragment from './ParentCellOrFragment'

export type AnswerWithFlagCellProps = {
  item: ResponseItem
  responderName?: string
  maxRows?: number
  disableCell?: boolean
  isAnswerStatusDisplay?: boolean
}

const formatNumberAccordingToLocaleButWithSpaceAsGroupSeparator = (answer: string | number) => {
  const partsOfFormattedNumber = new Intl.NumberFormat().formatToParts(Number(answer))
  return partsOfFormattedNumber.reduce((acc, part) => acc + (part.type === 'group' ? ' ' : part.value), '')
}

const formatCoordinates = (coordinates: CoordinateLocation[] | undefined, truncate = false): string => {
  if (!coordinates || coordinates.length === 0) return ''

  const sorted = coordinates.sort((a, b) => a.order - b.order)
  const displayCoordinates = truncate && sorted.length > 3 ? sorted.slice(0, 3) : sorted

  const formatted = displayCoordinates
    .map(coord => {
      const lat = coord.latitude?.toFixed(4) ?? ''
      const lng = coord.longitude?.toFixed(4) ?? ''
      const coords = `(${lat}, ${lng})`
      return coord.description ? `${coord.description}: ${coords}` : coords
    })
    .join(',\n')

  return truncate && sorted.length > 3 ? `${formatted}...` : formatted
}

export const getAnswerBasedOnQuestionType = (
  questionType: number,
  item: ResponseItem,
  isAnswerStatusDisplay?: boolean,
) => {
  if (isAnswerStatusDisplay && item.answer) {
    return item.answer
  }

  switch (questionType) {
    case QuestionType.Options:
      return item.selectedOptions?.join(', ')
    case QuestionType.Text:
      return item.answer
    case QuestionType.Number:
      return formatNumberAccordingToLocaleButWithSpaceAsGroupSeparator(item.answer)
  }
}

interface QuestionItemAnswerProps extends AnswerWithFlagCellProps {
  selectedOptionsAsChips?: boolean
  skippedAnswerAsCard?: boolean
}

export const QuestionItemAnswer: React.FC<QuestionItemAnswerProps> = ({
  item,
  maxRows,
  responderName,
  isAnswerStatusDisplay,
  selectedOptionsAsChips,
  skippedAnswerAsCard,
}) => {
  const { formatMessage } = useIntl()
  const { formatValueToDate } = useGetFormattedDate()
  const { palette, typography } = useTheme()
  const { openDrawer } = useDrawer()
  const { previousNumberAnswers } = usePreviousNumberAnswers()

  const isNumberQuestion = equalsCaseInsensitive(item.requestItem?.questionType?.name ?? '', QuestionTypes.Number)

  const previousAnswersThisQuestion = isNumberQuestion
    ? previousNumberAnswers.filter(
        p =>
          p.creatorOrganizationId === item.creatorOrganizationId &&
          p.requestItem.questionId === item.requestItem.questionId,
      )
    : []

  const questionType = item.requestItem.questionTypeId

  if (item.cannotAnswer) {
    if (skippedAnswerAsCard) {
      return (
        <SkippedQuestionCard
          answerStatus={item?.answerStatus}
          estimatedAnswerAt={item?.estimatedAnswerAt}
          isScraped={item?.isScraped}
          comment={item?.comment}
        />
      )
    }
    return item.estimatedAnswerAt ? (
      <Stack>
        <Typography variant="body1">{formatMessage({ id: 'reporting.planOnHaving' })}</Typography>
        <Box display="flex" alignItems="center" color={({ palette }) => palette.text.secondary}>
          <DateRangeOutlined fontSize="inherit" color="inherit" />
          <Typography variant="caption" ml={0.5} color="inherit">
            {formatValueToDate({ value: item?.estimatedAnswerAt ?? '' })}
          </Typography>
        </Box>
      </Stack>
    ) : (
      <Typography variant="body1" flexGrow={1} component="span">
        {`Skipped - ${formatMessage({ id: `reporting.answerStatus.${item.answerStatus}` })}`}
      </Typography>
    )
  }

  if (questionType === QuestionType.Options && selectedOptionsAsChips) {
    return (
      <Stack spacing={1} alignItems="flex-start" direction="column">
        {item.selectedOptions?.map(tag => (
          <Chip
            key={tag}
            label={
              <Box
                sx={{
                  py: 1,
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.5em',
                  fontWeight: typography.body1.fontWeight,
                }}
              >
                {tag}
              </Box>
            }
            sx={{
              mr: 1,
              height: 'auto',
              width: 'auto',
            }}
          />
        ))}
      </Stack>
    )
  }

  if (questionType === QuestionType.File) {
    return <FileAnswerContent item={item} />
  }

  if (questionType === QuestionType.Coordinates) {
    const coordinatesTruncated = formatCoordinates(item.coordinates, true)
    const coordinatesFull = formatCoordinates(item.coordinates, false)
    const hasTruncation = item.coordinates && item.coordinates.length > 3

    const handleCopy = async (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      await navigator.clipboard.writeText(coordinatesFull)
    }

    return (
      <Box display="flex" alignItems="flex-start" gap={1}>
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{coordinatesFull}</span>} placement="top" arrow>
          <Typography variant="body1" flexGrow={1} component="div" sx={{ whiteSpace: 'pre-line' }}>
            {coordinatesTruncated || '-'}
          </Typography>
        </Tooltip>
        {hasTruncation && (
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }} title={formatMessage({ id: 'general.copy' })}>
            <ContentCopy fontSize="small" />
          </IconButton>
        )}
      </Box>
    )
  }

  const answer = getAnswerBasedOnQuestionType(questionType, item, isAnswerStatusDisplay)

  return (
    <Box display="flex" alignItems="center">
      <Typography variant="body1" flexGrow={1} component="span">
        <TextTruncate
          line={maxRows}
          text={answer?.toString() ?? '-'}
          textTruncateChild={
            <span
              role="button"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                e.nativeEvent?.stopImmediatePropagation?.()
                openDrawer(
                  <DrawerViewAnswerDetails
                    companyName={responderName ?? item.creatorOrganization?.name ?? '-'}
                    question={item.requestItem.description}
                    answer={answer?.toString() ?? '-'}
                  />,
                  { showAboveDialogs: true },
                )
              }}
              style={{
                color: palette.common.black,
                fontWeight: typography.button.fontWeight,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {formatMessage({ id: 'general.readMore' })}
            </span>
          }
        />
      </Typography>
      {item.requestItem?.unit?.symbol && !isAnswerStatusDisplay && (
        <Typography variant="body1" ml={1}>
          {item.requestItem.unit.symbol}
        </Typography>
      )}
      {previousAnswersThisQuestion.length > 0 && (
        <DeviationIndicatorAnswer responseItem={item} previousAnswers={previousAnswersThisQuestion} />
      )}
    </Box>
  )
}

const AnswerWithFlagCell: React.FC<AnswerWithFlagCellProps> = ({
  item,
  maxRows = 2,
  disableCell,
  responderName,
  isAnswerStatusDisplay,
}) => {
  const { openDrawer } = useDrawer()
  const isWorldfavorRule = item.flagRule?.creatorOrganizationId === WF_ORGANIZATION_ID
  const isRedFlag = item?.flag === ReportFlagType.Red

  return (
    <ParentCellOrFragment
      disableCell={disableCell}
      sx={{
        height: 0,
        py: 1,
        px: isRedFlag ? 1 : 0,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={({ palette }) => ({
          height: '100%',
          width: '100%',
          py: 1,
          px: 2,
          borderRadius: 1,
          cursor: item?.isScraped === true ? 'pointer' : 'default',
          '&:hover': item?.isScraped === true ? { backgroundColor: palette.communication.light } : {},
          ...insertObjIf(isRedFlag && isWorldfavorRule, { backgroundColor: palette.communication.light }),
          ...insertObjIf(isRedFlag && !isWorldfavorRule, { backgroundColor: alpha(palette.error.main, 0.12) }),
        })}
        onClick={() => {
          if (item?.isScraped === true) {
            openDrawer(<DrawerViewAiScreenedAnswerDetail item={item} />, {
              showAboveDialogs: true,
              maxWidth: DRAWER_WIDTH.wide,
            })
          }
        }}
      >
        {item?.isScraped === true && <AutoAwesomeOutlined sx={{ color: palette.communication.dark }} />}
        <QuestionItemAnswer item={item} maxRows={maxRows} responderName={responderName} />
        {item.flag === ReportFlagType.Red && item.flagRule && (
          <FlagIndicator flagRules={[item.flagRule]} sx={{ ml: 1 }} />
        )}
      </Box>
    </ParentCellOrFragment>
  )
}

export default AnswerWithFlagCell
